import { SalesforceField } from '@/model/SalesforceField';
import SalesforceDropdown from './SalesforceDropdown';
import { memo, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'components/Generic/Forms/SalesforceIntegratedForms/FormContext';
import RepeatableSectionFieldDefiner from 'components/Generic/Forms/SalesforceIntegratedForms/RepeatableSectionFieldDefiner';
const SalesforceRepeatableSubSection = memo(function SalesforceRepeatableSubSection({
  fields,
  stepName,
}: SalesforceField) {
  const { formData, updateFormData, updateSummary } = useFormContext();

  const [sectionRepeatNumber, setSectionRepeatNumber] = useState(0);
  const [sectionFields, setSectionFields] = useState<SalesforceField[]>([]);
  const items = [
    {
      name: 0,
      displayName: 0,
      fields: {
        salesforceValue: { value: 0 },
        preselectedValue: { value: true },
      },
    },
  ];
  // Populate items just like Vue logic
  for (let i = 1; i <= +fields.maxRepeats.value; i++) {
    items.push({
      name: i,
      displayName: i,
      fields: {
        salesforceValue: {
          value: i,
        },
        preselectedValue: {
          value: i % 2 === 0, // Matches original Vue logic (even if odd)
        },
      },
    });
  }
  const dropdownOptions = {
    ...fields,
    items: items,
    isRequired: {
      value: true,
    },
    title: {
      value: fields.dropdownText.value,
    },
    salesforceFieldId: {
      value: fields.dropdownText.value,
    },
  };

  const defaultSelectedOption = {
    label: dropdownOptions.items[0].displayName,
    value: dropdownOptions.items[0].fields.salesforceValue.value,
  };

  const getEnteredFieldsList = () => {
    const list = [];
    for (const [key] of Object.entries(formData.records[0])) {
      list.push(key);
    }
    return list;
  };
  const onSelect = (value: string) => {
    const numericValue = +value;
    setSectionRepeatNumber(numericValue);
    if (numericValue < sectionRepeatNumber) {
      setTimeout(() => {
        removeRepeatedSectionsValue();
      }, 10);
    }
  };

  const removeRepeatedSectionsValue = () => {
    const enteredFieldsList = getEnteredFieldsList();
    const currentStepSectionFieldsIds = sectionFields.map(
      (field: SalesforceField) =>
        field.fields.salesforceFieldId && field.fields.salesforceFieldId.value
    );
    enteredFieldsList.forEach((field) => {
      const inputFieldElement = document.getElementById(field);
      if (!inputFieldElement && currentStepSectionFieldsIds.includes(field)) {
        updateFormData({
          fieldName: field,
          value: '',
          // @ts-expect-error TODO: seems to be a bug
          summaryData: { stepName: stepName, fieldName: field },
        });
        updateSummary({
          stepName: stepName,
          // @ts-expect-error TODO: seems to be a bug
          fieldName: '',
        });
      }
    });
  };

  const updateSectionFields = useCallback(
    (fields: SalesforceField[], index = 1, result: SalesforceField[] = []) => {
      if (index > +sectionRepeatNumber) {
        setSectionFields(result);
        return;
      }

      const processField = (field: SalesforceField): SalesforceField => {
        const newField = { ...field, fields: { ...field.fields } };

        if (newField.fields.richTextArea?.value?.includes('{n}')) {
          newField.fields.richTextArea = {
            ...newField.fields.richTextArea,
            value: newField.fields.richTextArea.value.replace('{n}', String(index)),
          };
        }

        if (newField.fields.salesforceFieldId?.value?.includes('{n}')) {
          newField.fields.salesforceFieldId = {
            ...newField.fields.salesforceFieldId,
            value: newField.fields.salesforceFieldId.value.replace('{n}', String(index)),
          };
        }

        // Recursively process nested items if they exist
        if (Array.isArray(newField.fields.items)) {
          newField.fields.items = newField.fields.items.map(processField);
        }

        return newField;
      };

      const updatedFields = fields.map(processField);

      updateSectionFields(fields, index + 1, [...result, ...updatedFields]);
    },
    [sectionRepeatNumber]
  );

  useEffect(() => {
    updateSectionFields(fields.items);
  }, [sectionRepeatNumber, fields.items, updateSectionFields]);

  return (
    <section id="repeatableSection">
      <SalesforceDropdown
        stepName={stepName}
        fields={dropdownOptions as any}
        defaultValue={defaultSelectedOption}
        onValueSelected={onSelect}
      />
      <div className="nested-section">
        {sectionFields.map((section, index) => (
          <RepeatableSectionFieldDefiner
            key={index}
            nestedField={true}
            templateName={section.template}
            sectionName={section.displayName}
            fields={section.fields}
            stepName={stepName}
          />
        ))}
      </div>
    </section>
  );
});

export default SalesforceRepeatableSubSection;
